import type { Capture, CaptureDiagnostics } from '../audio/capture';
import { RingBuffer } from '../dsp/ring';

const ODF_TRACE = 690; // ≈ 8 s při 86 rámcích/s

export function rmsDb(samples: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i];
  const rms = Math.sqrt(sum / Math.max(1, samples.length));
  return Math.max(-120, 20 * Math.log10(Math.max(rms, 1e-6)));
}

export function settingsRows(d: CaptureDiagnostics): [string, string][] {
  const yn = (v: boolean | undefined) => (v === undefined ? 'nehlášeno' : v ? 'ANO' : 'ne');
  return [
    ['AudioContext.sampleRate', `${d.contextSampleRate} Hz`],
    ['track sampleRate', d.trackSampleRate === undefined ? 'nehlášeno' : `${d.trackSampleRate} Hz`],
    ['echoCancellation', yn(d.echoCancellation)],
    ['noiseSuppression', yn(d.noiseSuppression)],
    ['autoGainControl', yn(d.autoGainControl)],
    ['channelCount', d.channelCount === undefined ? 'nehlášeno' : String(d.channelCount)],
  ];
}

export interface DiagnosticsPanel {
  pushOdf(values: Float32Array): void;
  pushLevel(rms: number): void;
  destroy(): void;
}

const LEVEL_HISTORY = 300; // ~5 s při 60 fps
const SPECTRUM_MAX_HZ = 8000;

export function mountDiagnostics(el: HTMLElement, capture: Capture): DiagnosticsPanel {
  const { context, source, diagnostics } = capture;
  el.innerHTML = `
    <table class="diag">${settingsRows(diagnostics)
      .map(([k, v]) => `<tr><th>${k}</th><td>${v}</td></tr>`)
      .join('')}</table>
    ${diagnostics.warnings.map((w) => `<p class="warn">⚠ ${w}</p>`).join('')}
    <p>Úroveň: <strong data-level>–</strong> dBFS</p>
    <p>Spektrum 0–${SPECTRUM_MAX_HZ / 1000} kHz (žluté čáry: 80 Hz, 200 Hz, 1 kHz)</p>
    <canvas data-spectrum width="512" height="120"></canvas>
    <p>Průběh hlasitosti, −60…0 dBFS</p>
    <canvas data-level-history width="512" height="60"></canvas>
    <p>ODF (posledních 8 s), úroveň z workletu: <strong data-worklet-level>–</strong> dBFS</p>
    <canvas data-odf width="690" height="120"></canvas>
    <p><small>${navigator.userAgent}</small></p>`;

  const analyser = context.createAnalyser();
  analyser.fftSize = 2048;
  analyser.smoothingTimeConstant = 0.5;
  source.connect(analyser);

  const freq = new Uint8Array(analyser.frequencyBinCount);
  const time = new Float32Array(analyser.fftSize);
  const hzPerBin = context.sampleRate / analyser.fftSize;
  const binsToDraw = Math.min(freq.length, Math.ceil(SPECTRUM_MAX_HZ / hzPerBin));
  const history = new Float32Array(LEVEL_HISTORY).fill(-120);
  let historyPos = 0;

  const levelEl = el.querySelector<HTMLElement>('[data-level]')!;
  const spectrumCtx = el.querySelector<HTMLCanvasElement>('[data-spectrum]')!.getContext('2d')!;
  const historyCtx = el.querySelector<HTMLCanvasElement>('[data-level-history]')!.getContext('2d')!;

  const odf = new RingBuffer(ODF_TRACE);
  const odfCopy = new Float32Array(ODF_TRACE);
  let odfMax = 1e-3;
  const odfCtx = el.querySelector<HTMLCanvasElement>('[data-odf]')!.getContext('2d')!;
  const workletLevelEl = el.querySelector<HTMLElement>('[data-worklet-level]')!;

  let raf = 0;
  const frame = (): void => {
    analyser.getByteFrequencyData(freq);
    analyser.getFloatTimeDomainData(time);
    const db = rmsDb(time);
    history[historyPos] = db;
    historyPos = (historyPos + 1) % LEVEL_HISTORY;
    levelEl.textContent = db.toFixed(1);
    drawSpectrum(spectrumCtx, freq, binsToDraw, hzPerBin);
    drawLevelHistory(historyCtx, history, historyPos);
    const n = odf.read(odfCopy);
    let m = 0;
    for (let i = 0; i < n; i++) if (odfCopy[i] > m) m = odfCopy[i];
    odfMax = Math.max(m, odfMax * 0.995); // pomalý pokles měřítka
    drawOdf(odfCtx, odfCopy, n, odfMax);
    raf = requestAnimationFrame(frame);
  };
  raf = requestAnimationFrame(frame);

  return {
    pushOdf(values) {
      odf.pushAll(values);
    },
    pushLevel(rms) {
      workletLevelEl.textContent = (20 * Math.log10(Math.max(rms, 1e-6))).toFixed(1);
    },
    destroy() {
      cancelAnimationFrame(raf);
      source.disconnect(analyser);
      analyser.disconnect();
    },
  };
}

function drawSpectrum(ctx: CanvasRenderingContext2D, data: Uint8Array, bins: number, hzPerBin: number): void {
  const { width, height } = ctx.canvas;
  ctx.fillStyle = '#111';
  ctx.fillRect(0, 0, width, height);
  const barW = width / bins;
  ctx.fillStyle = '#33ddff';
  for (let k = 0; k < bins; k++) {
    const h = (data[k] / 255) * height;
    ctx.fillRect(k * barW, height - h, Math.max(1, barW - 0.5), h);
  }
  ctx.fillStyle = '#ffcc00';
  for (const hz of [80, 200, 1000]) ctx.fillRect((hz / hzPerBin) * barW, 0, 1, height);
}

function drawOdf(ctx: CanvasRenderingContext2D, values: Float32Array, n: number, max: number): void {
  const { width, height } = ctx.canvas;
  ctx.fillStyle = '#111';
  ctx.fillRect(0, 0, width, height);
  ctx.fillStyle = '#33ddff';
  const barW = width / values.length;
  for (let i = 0; i < n; i++) {
    const h = (values[i] / max) * height;
    ctx.fillRect(i * barW, height - h, Math.max(1, barW), h);
  }
}

function drawLevelHistory(ctx: CanvasRenderingContext2D, history: Float32Array, pos: number): void {
  const { width, height } = ctx.canvas;
  ctx.fillStyle = '#111';
  ctx.fillRect(0, 0, width, height);
  ctx.strokeStyle = '#33ddff';
  ctx.beginPath();
  for (let i = 0; i < history.length; i++) {
    const db = history[(pos + i) % history.length];
    const y = height - ((Math.max(-60, db) + 60) / 60) * height;
    const x = (i / (history.length - 1)) * width;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.stroke();
}
